import { Effect, Exit } from "effect";
import type { StationClientFailure } from "../../api/station/model.ts";
import type { WorkbenchCore, CreatedWorkspace } from "./workbench-core.ts";

export const onlyMissing = (result: Exit.Exit<unknown, StationClientFailure>): boolean => {
  if (!Exit.isFailure(result) || result.cause.reasons.length !== 1) return false;
  const reason = result.cause.reasons[0];
  return reason?._tag === "Fail" && (reason.error._tag === "StationHttpError" || reason.error._tag === "WorkspaceHttpError") && reason.error.status === 404;
};
const fail = (core: WorkbenchCore, message: string) => core.update(current => ({ ...current, bootstrapError: message, booted: true }));

export const startFreshWorkbench = Effect.fn("Frontend.StationWorkbench.startFresh")(function* (core: WorkbenchCore, stationId: string) {
  if (!(yield* core.active())) return;
  yield* core.accepted.join(Effect.gen(function* () {
    if (!(yield* core.active())) return;
    yield* core.update(current => ({ ...current, stationId }));
    const drafts = yield* core.io.starters(stationId);
    const results = yield* Effect.forEach(drafts, draft => Effect.exit(core.create(draft)), { concurrency: 3 });
    const created: CreatedWorkspace[] = [];
    for (const result of results) if (Exit.isSuccess(result) && result.value.persisted) created.push(result.value);
    const first = created[0];
    if (created.length !== drafts.length || first === undefined) return yield* fail(core, "Could not create the initial workspaces. Reload to try again.");
    const list = created.map(workspace => workspace.id);
    const written = yield* Effect.exit(core.station.persist(stationId, { workspaces: list, active: first.id }));
    if (Exit.isFailure(written)) return yield* fail(core, "Could not save the new workerstation session. Reload to try again.");
    if (!(yield* core.active())) return;
    yield* core.update(current => ({ ...current, workspaces: list }));
    yield* core.hydrate(first.target, first.draft, true, true);
  }));
});

export const bootstrapWorkbench = Effect.fn("Frontend.StationWorkbench.bootstrap")(function* (core: WorkbenchCore) {
  const route = yield* core.io.route;
  if (route._tag === "Launcher") return yield* core.update(current => ({ ...current, showLauncher: true, booted: true }));
  if (route._tag === "Fresh") {
    const id = yield* core.io.newWorkspaceId; yield* core.io.redirect(id); yield* startFreshWorkbench(core, id);
    return yield* core.update(current => ({ ...current, booted: true }));
  }
  const id = route.stationId;
  yield* core.io.visit(id);
  const station = yield* Effect.exit(core.queries.run(core.api.station(id)));
  if (Exit.isFailure(station) && !onlyMissing(station)) return yield* fail(core, "Could not load the workerstation session. Reload to try again.");
  if (Exit.isSuccess(station) && station.value.workspaces.length) {
    yield* core.update(current => ({ ...current, stationId: id }));
    const list = station.value.workspaces, preferred = station.value.active && list.includes(station.value.active) ? station.value.active : list[0];
    let unavailable = false;
    for (const workspaceId of [preferred, ...list.filter(value => value !== preferred)]) {
      if (workspaceId === undefined) continue;
      const workspace = yield* Effect.exit(core.queries.run(core.api.workspace(workspaceId, id)));
      if (Exit.isSuccess(workspace)) {
        if (!(yield* core.active())) return;
        const target = yield* core.target(workspaceId);
        yield* core.update(current => ({ ...current, workspaces: list })); yield* core.hydrate(target, workspace.value);
        return yield* core.update(current => ({ ...current, booted: true }));
      }
      unavailable ||= !onlyMissing(workspace);
    }
    if (unavailable) return yield* fail(core, "Could not load this workerstation's workspaces. Reload to try again.");
  }
  const legacy = yield* Effect.exit(core.queries.run(core.api.workspace(id)));
  if (Exit.isFailure(legacy) && !onlyMissing(legacy)) return yield* fail(core, "Could not determine whether this workerstation already exists. Reload to try again.");
  if (Exit.isSuccess(legacy)) {
    if (!(yield* core.active())) return;
    return yield* core.accepted.join(Effect.gen(function* () {
      if (!(yield* core.active())) return;
      yield* core.update(current => ({ ...current, stationId: id }));
      const result = yield* Effect.exit(core.station.persist(id, { workspaces: [id], active: id }));
      if (Exit.isFailure(result)) return yield* fail(core, "Could not create a workerstation session for this legacy workspace. Reload to try again.");
      if (!(yield* core.active())) return;
      yield* core.update(current => ({ ...current, workspaces: [id] })); yield* core.hydrate(yield* core.target(id), legacy.value);
      yield* core.update(current => ({ ...current, booted: true }));
    }));
  }
  yield* startFreshWorkbench(core, id); yield* core.update(current => ({ ...current, booted: true }));
});
